import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
  ClientModuleResponseModel,
  useGetAdminHubLinksFunctionRequest,
  useUpsertHubLinksFunctionRequest,
  useUploadGenericImageFunctionRequest,
} from "shared/request/myHealthyAdvantageApi";
import { useTranslation } from "react-i18next";
import Button from "shared/UI/Buttons/Button";
import { Input } from "shared/UI/Input/Input";
import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import { urlRegex } from "shared/utils/regex";
import { getLinkImageSrc } from "shared/utils/url";
import { ReactComponent as TrashButton } from "shared/assets/Icons/trash-button.svg";
import { ChooseImageModal } from "../ChooseImageModal/ChooseImageModal";

export type HubLinkRow = {
  linkUrl: string;
  image: string;
};

export type HubTypeLinksSectionHandle = {
  isDirty: boolean;
  reset: VoidFunction;
  validate: () => boolean;
  save: () => Promise<void>;
};

type ImageModalTarget = { type: "add" } | { type: "edit"; index: number };

type HubTypeLinksSectionProps = {
  clientId: string;
  clientCode: string;
  module: ClientModuleResponseModel;
  onDirtyChange: (moduleId: string, isDirty: boolean) => void;
};

export const HubTypeLinksSection = forwardRef<HubTypeLinksSectionHandle, HubTypeLinksSectionProps>(
  ({ clientId, clientCode, module, onDirtyChange }, ref) => {
    const { t } = useTranslation();
    const { data, mutate } = useGetAdminHubLinksFunctionRequest({ clientId, moduleId: module.id });
    const { trigger: uploadImage } = useUploadGenericImageFunctionRequest({ clientCode });
    const { trigger: upsertHubLinks } = useUpsertHubLinksFunctionRequest();

    const savedLinks: HubLinkRow[] = useMemo(() => data?.hubLinks?.map((link) => ({ linkUrl: link.linkUrl, image: link.image })) ?? [], [data]);

    const [links, setLinks] = useState<HubLinkRow[]>(savedLinks);
    const [linkUrl, setLinkUrl] = useState("");
    const [imageModalTarget, setImageModalTarget] = useState<ImageModalTarget | null>(null);
    const [hasSyncedInitialLinks, setHasSyncedInitialLinks] = useState(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    useEffect(() => {
      if (data && !hasSyncedInitialLinks) {
        setLinks(savedLinks);
        setHasSyncedInitialLinks(true);
      }
    }, [data, savedLinks, hasSyncedInitialLinks]);

    const isDirty = useMemo(() => JSON.stringify(links) !== JSON.stringify(savedLinks), [links, savedLinks]);

    useEffect(() => onDirtyChange(module.id, isDirty), [module.id, isDirty, onDirtyChange]);

    const hasPendingUnaddedLink = linkUrl.trim().length > 0;
    const isNewLinkUrlInvalid = hasPendingUnaddedLink && !urlRegex.test(linkUrl.trim());
    const invalidLinkIndexes = useMemo(
      () => links.reduce<number[]>((acc, link, index) => (urlRegex.test(link.linkUrl.trim()) ? acc : [...acc, index]), []),
      [links]
    );
    const isSectionValid = !hasPendingUnaddedLink && invalidLinkIndexes.length === 0;

    useImperativeHandle(ref, () => ({
      isDirty,
      reset: () => {
        setLinks(savedLinks);
        setLinkUrl("");
        setShowValidationErrors(false);
      },
      validate: () => {
        setShowValidationErrors(true);
        return isSectionValid;
      },
      save: async () => {
        const hubLinks = links.map((link) => ({ linkUrl: link.linkUrl, image: link.image }));
        await upsertHubLinks({ body: { clientId, hubModuleId: module.id, hubLinks } });
        setLinks(hubLinks);
        setShowValidationErrors(false);
        await mutate();
      },
    }));

    const updateLinkUrl = (indexToUpdate: number, newUrl: string) => {
      setLinks((prev) => prev.map((link, index) => (index === indexToUpdate ? { ...link, linkUrl: newUrl } : link)));
    };

    const handleImageModalAdd = async (file: File) => {
      const formData = new FormData();
      formData.append("hubsite", file);
      const response = await uploadImage({ body: formData });
      const imageUrl = response?.data?.url ?? "";

      if (imageModalTarget?.type === "edit") {
        const indexToUpdate = imageModalTarget.index;
        setLinks((prev) => prev.map((link, index) => (index === indexToUpdate ? { ...link, image: imageUrl } : link)));
      } else {
        setLinks((prev) => [...prev, { linkUrl, image: imageUrl }]);
        setLinkUrl("");
      }
      setImageModalTarget(null);
    };

    const removeLink = (indexToRemove: number) => {
      setLinks((prev) => prev.filter((_, index) => index !== indexToRemove));
    };

    const hubTypeLabel = t<string>(`clientModules.${module.key}`);

    return (
      <div className="mt-8">
        <label className="font-bold">{t("hubSites.addLinkLabel", { hubType: hubTypeLabel })}</label>
        <div className="flex flex-row mt-4">
          <div className="w-1/2">
            <Input
              aria-label={t("hubSites.addLinkAriaLabel", { hubType: hubTypeLabel })}
              placeholder={t("hubSites.linkUrlPlaceholder")}
              onChange={(e) => setLinkUrl(e.target.value)}
              value={linkUrl}
            />
            {showValidationErrors && isNewLinkUrlInvalid && <ErrorAlert content={t("hubSites.linkUrlInvalidError")} />}
            {showValidationErrors && hasPendingUnaddedLink && !isNewLinkUrlInvalid && <ErrorAlert content={t("hubSites.pendingLinkUrlError")} />}
          </div>
          <div className="ml-2 w-[50px]">
            <Button
              disabled={linkUrl.trim().length === 0 || isNewLinkUrlInvalid}
              text="+"
              aria-label={t("hubSites.addLinkButtonAriaLabel", { hubType: hubTypeLabel })}
              onClick={() => setImageModalTarget({ type: "add" })}
            />
          </div>
        </div>
        {links.map((link, index) => (
          <div className="flex flex-row gap-4 mt-4 w-3/4" key={index}>
            <div className="w-1/2">
              <Input placeholder={t("hubSites.linkUrlPlaceholder")} onChange={(e) => updateLinkUrl(index, e.target.value)} value={link.linkUrl} />
              {showValidationErrors && invalidLinkIndexes.includes(index) && <ErrorAlert content={t("hubSites.linkUrlInvalidError")} />}
            </div>
            <button
              type="button"
              className="w-1/4"
              aria-label={t("hubSites.editImageAriaLabel", { link: link.linkUrl })}
              onClick={() => setImageModalTarget({ type: "edit", index })}
            >
              <img className="h-[50px] w-full object-contain rounded-lg" src={getLinkImageSrc(link.image, clientCode)} alt={link.linkUrl} />
            </button>
            <div className="mt-1 ml-2">
              <TrashButton
                role="button"
                className="icon-button-sm"
                aria-label={t("hubSites.removeLinkAriaLabel", { link: link.linkUrl })}
                onClick={() => removeLink(index)}
              />
            </div>
          </div>
        ))}
        {imageModalTarget && <ChooseImageModal onAdd={handleImageModalAdd} onCancel={() => setImageModalTarget(null)} />}
      </div>
    );
  }
);
